import React, { useCallback } from 'react'
import { CustomerType, SimpleCustomerLookupFragment, SimpleCustomersLookupDocument } from 'src/api/api.generated'
import BaseCustomerLookup from './base/base-customer-lookup'
import { FooterOption } from './option/simple-customer-lookup-footer-option'
import { SimpleCustomerLookupOption } from './option/simple-customer-lookup-option'
import { splitCustomerName } from './utils-lookup'

export type SimpleCustomerLookupProps = {
  customerType: CustomerType
  value: SimpleCustomerLookupFragment | { id: string } | null
  onChange: (value: SimpleCustomerLookupFragment | null) => void
  createAction?: boolean
  updateAction?: boolean
  disabled?: boolean
  error?: boolean
  minSearchParameterLenght?: number
  clearOnBlur?: boolean
  onBlur?: (name: { lastName: string; firstName: string }) => void
  placeholder?: string
  caption?: string
  showEmail?: boolean
  showPhoneNumber?: boolean
  showTags?: boolean
  showRelatedCompany?: boolean
  size?: 'small' | 'medium'
  selectableCustomers?: string[]
  noOptionsText?:React.ReactNode
}

const SimpleCustomerLookup = React.memo(SimpleCustomerLookupComponent, (prev, next) => {
  if (prev.customerType !== next.customerType) {
    return false
  }

  // Commentata evitare rendering indesiderati che causano il ripristino del valore precedente in caso di cancellazione tramite la funzione clear della lookup.
  // if (prev.onChange !== next.onChange) {
  //   return false
  // }

  if (prev.createAction !== next.createAction) {
    return false
  }
  if (prev.updateAction !== next.updateAction) {
    return false
  }
  if (prev.disabled !== next.disabled) {
    return false
  }
  if (prev.error !== next.error) {
    return false
  }
  if (prev.minSearchParameterLenght !== next.minSearchParameterLenght) {
    return false
  }
  if (prev.clearOnBlur !== next.clearOnBlur) {
    return false
  }

  if (JSON.stringify(prev.value) !== JSON.stringify(next.value)) {
    return false
  }
  return true
})

export default SimpleCustomerLookup

function SimpleCustomerLookupComponent({
  customerType,
  value,
  onChange,
  createAction,
  updateAction,
  disabled,
  error,
  minSearchParameterLenght,
  clearOnBlur,
  onBlur,
  placeholder,
  caption,
  showEmail,
  showPhoneNumber,
  showTags,
  showRelatedCompany,
  size,
  selectableCustomers,
  noOptionsText
}: SimpleCustomerLookupProps) {
  const minTextLenght = minSearchParameterLenght || 3

  const onBlurHandler = useCallback(
    (inputValue: string) => {
      if (!value?.id && onBlur && inputValue.length > minTextLenght) {
        onBlur(splitCustomerName(inputValue))
      }
    },
    [onBlur],
  )

  return (
    <BaseCustomerLookup<SimpleCustomerLookupFragment> //
      customerType={customerType}
      value={value}
      onChange={onChange}
      queryDocument={SimpleCustomersLookupDocument}
      renderOption={(option) => <SimpleCustomerLookupOption option={option} showEmail={showEmail} showPhoneNumber={showPhoneNumber} showTags={showTags} showRelatedCompany={showRelatedCompany} />}
      footerOption={
        !value?.id && placeholder && caption
          ? (inputValue, onClose) => (
              <FooterOption //
                text={inputValue}
                onClick={(e) => {
                  e.preventDefault()
                  onClose()
                }}
                disabled={false}
                caption={caption}
                placeholder={placeholder}
              />
            )
          : undefined
      }
      createAction={createAction}
      updateAction={updateAction}
      onCustomerUpserted={(customer) => customer}
      disabled={disabled}
      error={error}
      minSearchParameterLenght={minTextLenght}
      clearOnBlur={clearOnBlur}
      onBlur={onBlurHandler}
      size={size}
      selectableCustomers={selectableCustomers}
      noOptionsText={noOptionsText ? <i>{noOptionsText}</i> : undefined}
    />
  )
}
